<template>
	<div
		class="listWidget"
		ref="myDOM"
		@dragover="handleDragOver"
		@drop="handleDrop"
		@dragleave="handleDragLeave"
		@click="selectCurWidget"
	>
		<component v-for="node in me.showNodes" :key="node.id" :is="node.type" :me="node" :parentType="me.type" />
	</div>
</template>

<script lang="ts">
import { defineComponent, inject, onMounted, PropType, reactive, ref, watch } from 'vue';
import { dataKey, ListWidget, widgetType, State, WidgetSpacer } from '../types';
import createWidget from './createWidget';
import apportion from '../util/apportion';
import getBase64Image from '../util/getBase64Image';
export default defineComponent({
	props: {
		me: { type: Object as PropType<ListWidget>, required: true },
	},
	setup(props) {
		const myDOM = ref<HTMLElement>(); // 指代当前组件所在的节点
		const me = reactive(props.me);
		const state: State = inject<any>(dataKey.STATE);

		// 处理选中
		const selectCurWidget = (e: MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();
			state.curWidget = me;
		};

		// 处理组件属性
		onMounted(() => {
			const target = myDOM.value as HTMLElement;
			let childrenCount = me.realNodes.length;
			let lastTotalPadding =
				me.attrs.padding.top + me.attrs.padding.bottom + me.attrs.padding.right + me.attrs.padding.left;
			let spacing = me.attrs.spacing;
			watch(
				[me.realNodes, me.attrs, me.size],
				() => {
					// spacing、nodes
					if (me.realNodes.length !== childrenCount || spacing !== me.attrs.spacing) {
						childrenCount = me.realNodes.length;
						spacing = me.attrs.spacing;

						me.showNodes = me.realNodes.map(v => v);
						if (spacing > 0) {
							me.showNodes.forEach((node, index, array) => {
								if (index != childrenCount - 1) {
									const widgetSpacer = createWidget(widgetType.SPACER) as WidgetSpacer;
									widgetSpacer.attrs.length = spacing;
									array.splice(index * 2 - 1, 0, widgetSpacer);
								}
							});
						}
						apportion(me);
					}

					// width、height
					target.style.width = me.size.width + 'px';
					target.style.height = me.size.height + 'px';

					// backgroundColor
					target.style.backgroundColor = me.attrs.backgroundColor;

					// backgroundGradient
					target.style.backgroundImage = '';
					const angle = me.attrs.backgroundGradient.angle + 'deg';
					const colors = me.attrs.backgroundGradient.colors.split(',');
					const locations = me.attrs.backgroundGradient.locations.split(',');
					const colorAndLocation = colors
						.reduce((preValue, curValue, index) => {
							const location = Number(locations[index]) * 100 + '%';
							return preValue + curValue + ' ' + location + ',';
						}, '')
						.slice(0, -1);
					target.style.backgroundImage = 'linear-gradient(' + angle + ', ' + colorAndLocation + ' )';

					// backgroundImage
					const imageURL = me.attrs.backgroundImage;
					if (imageURL) {
						const bgImage = new Image();
						bgImage.onload = () => {
							const imageDate = getBase64Image(bgImage);
							target.style.backgroundImage = 'url(' + imageDate + ')';
							me.bgImageDate = imageDate.slice(22);
						};
						bgImage.src = imageURL;
					}

					// padding
					target.style.paddingTop = me.attrs.padding.top + 'px';
					target.style.paddingLeft = me.attrs.padding.left + 'px';
					target.style.paddingRight = me.attrs.padding.right + 'px';
					target.style.paddingBottom = me.attrs.padding.bottom + 'px';
					const totalPadding =
						me.attrs.padding.top + me.attrs.padding.bottom + me.attrs.padding.right + me.attrs.padding.left;
					if (lastTotalPadding !== totalPadding) {
						lastTotalPadding = totalPadding;
						apportion(me);
					}

					// 若仅包含stack和spacer，则垂直和水平居中对齐（默认）
					// 若存在image、text或date，则靠左垂直居中对齐
					const types: any = {
						[widgetType.IMAGE]: true,
						[widgetType.TEXT]: true,
						[widgetType.DATE]: true,
					};
					if (me.realNodes.find(node => types[node.type])) {
						target.style.alignItems = 'flex-start';
					} else {
						target.style.alignItems = 'center';
					}
				},
				{ deep: true, immediate: true }
			);
		});

		// 处理新增组件
		const handleDragOver = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();

			// 选中高亮
			const target = myDOM.value as HTMLElement;
			target.style.opacity = '0.5';

			(e.dataTransfer as DataTransfer).dropEffect = 'copy';
		};
		const handleDrop = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();

			// 取消高亮
			const target = myDOM.value as HTMLElement;
			target.style.opacity = '';

			// 将新增组件插入到容器中
			const dataTransfer = e.dataTransfer as DataTransfer;
			const type = dataTransfer.getData(dataKey.TYPEOFADDEDWIDGET) as widgetType;
			const newWidget = createWidget(type);
			if (newWidget) me.realNodes.push(newWidget);
		};
		const handleDragLeave = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();

			// 取消高亮
			const target = myDOM.value as HTMLElement;
			target.style.opacity = '';
		};

		return {
			myDOM,
			handleDragOver,
			handleDrop,
			handleDragLeave,
			selectCurWidget,
		};
	},
});
</script>

<style scoped>
.listWidget {
	display: flex;
	flex-direction: column;
	justify-content: center;
	overflow: hidden;
	flex-wrap: nowrap;
	border-radius: 20px;
	box-sizing: border-box;
	background-size: cover;
}
.listWidget > * {
	flex-grow: 0;
	flex-shrink: 0;
}
</style>
